import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground, addTextGlow } from '@/utils/arcadeBackground';
import { createBackButton } from '@/ui/buttons';
import { isDiscordActivity } from '@/utils/discordActivity';
import { getDiscordGuildId, setDiscordIdlePresence } from '@/discord';
import { getActiveMessageSlots, type MessageEntry } from '@/config/messages';
import { fetchLeaderboard, type LeaderboardRun } from '@/services/leaderboard';
import { formatDailyDisplayDate, getDailyLosAngelesDate } from '@/utils/dailyDate';

type DailyDateEntry = {
  date: string;
  label: string;
};

type LeaderboardView = 'daily' | 'messages';

type LeaderboardRow = {
  bg: Phaser.GameObjects.Rectangle;
  rank: Phaser.GameObjects.Text;
  name: Phaser.GameObjects.Text;
  score: Phaser.GameObjects.Text;
  accuracy: Phaser.GameObjects.Text;
  grade: Phaser.GameObjects.Text;
};

export class LeaderboardScene extends Phaser.Scene {
  private messageSlots: MessageEntry[] = [];
  private visibleMessageSlots: MessageEntry[] = [];
  private messageButtons: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private dateButtons: { bg: Phaser.GameObjects.Rectangle; label: Phaser.GameObjects.Text }[] = [];
  private rows: LeaderboardRow[] = [];
  private statusText?: Phaser.GameObjects.Text;
  private rightHeaderTitle?: Phaser.GameObjects.Text;
  private rightHeaderSubtitle?: Phaser.GameObjects.Text;
  private refreshTimer?: Phaser.Time.TimerEvent;
  private lastLoadedLeaderboardKey: string | null = null;
  private guildId: string | null = null;
  private dailyDates: DailyDateEntry[] = [];
  private selectedMessageIndex = 0;
  private selectedDateIndex = 0;
  private activeView: LeaderboardView = 'daily';
  private dailyPill?: Phaser.GameObjects.Rectangle;
  private dailyPillLabel?: Phaser.GameObjects.Text;
  private messagesPill?: Phaser.GameObjects.Rectangle;
  private messagesPillLabel?: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'LeaderboardScene' });
  }

  create(): void {
    // Reset state from previous scene instance
    this.messageButtons = [];
    this.dateButtons = [];
    this.rows = [];
    this.dailyDates = [];
    this.visibleMessageSlots = [];
    this.selectedMessageIndex = 0;
    this.selectedDateIndex = 0;
    this.activeView = 'daily';
    this.lastLoadedLeaderboardKey = null;
    this.guildId = null;
    this.dailyPill = undefined;
    this.dailyPillLabel = undefined;
    this.messagesPill = undefined;
    this.messagesPillLabel = undefined;
    this.rightHeaderTitle = undefined;
    this.rightHeaderSubtitle = undefined;
    this.refreshTimer?.destroy();
    this.refreshTimer = undefined;

    this.cameras.main.fadeIn(300, 5, 7, 22);
    setDiscordIdlePresence();
    drawArcadeBackground(this);

    const panelWidth = 1040;
    const panelHeight = 560;
    const panelX = GAME_WIDTH / 2;
    const panelY = GAME_HEIGHT / 2 + 20;
    const panel = this.add.rectangle(panelX, panelY, panelWidth, panelHeight, THEME.colors.backgroundLightHex, 0.85);
    panel.setStrokeStyle(3, THEME.colors.magentaHex);
    panel.setDepth(-1);

    const title = this.add.text(GAME_WIDTH / 2, 70, 'LEADERBOARDS', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.titleLarge,
      color: THEME.colors.cyan,
    }).setOrigin(0.5);
    addTextGlow(title, THEME.colors.cyan, 16);

    this.guildId = getDiscordGuildId();
    if (!isDiscordActivity() || !this.guildId) {
      const notice = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'Leaderboards require a Discord server.', {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.bodyLarge,
        color: THEME.colors.textMuted,
      }).setOrigin(0.5);
      notice.setDepth(1);

      createBackButton(this, {
        x: 100,
        y: 60,
        onClick: () => this.scene.start('MenuScene'),
      });
      return;
    }

    const panelLeft = panelX - panelWidth / 2;
    const panelRight = panelX + panelWidth / 2;
    const messageRowWidth = 260;
    const messageLeft = panelLeft + 32;
    const leftX = messageLeft + messageRowWidth / 2;
    const listTop = 240;
    const rowHeight = 44;
    const tableLeft = messageLeft + messageRowWidth + 40;
    const tableWidth = panelRight - 32 - tableLeft;
    const rightX = tableLeft + tableWidth / 2;
    const headerTop = listTop - 116;
    const headerMessageY = listTop - 82;
    const headerRowY = listTop - 48;

    const pillWidth = messageRowWidth;
    const pillHeight = 42;
    const pillSpacing = 8;
    const pillTop = headerTop + 10;
    const dailyPillY = pillTop;
    const messagesPillY = pillTop + pillHeight + pillSpacing;
    const pillX = messageLeft + pillWidth / 2;
    const pillLabelX = messageLeft + 12;

    this.dailyPill = this.add.rectangle(pillX, dailyPillY, pillWidth, pillHeight, THEME.colors.backgroundPanelHex, 0.95);
    this.dailyPill.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    this.dailyPill.setInteractive({ useHandCursor: true });
    this.dailyPillLabel = this.add.text(pillLabelX, dailyPillY, 'Daily Challenges', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);
    this.dailyPill.on('pointerdown', () => this.setActiveView('daily'));

    this.messagesPill = this.add.rectangle(pillX, messagesPillY, pillWidth, pillHeight, THEME.colors.backgroundPanelHex, 0.95);
    this.messagesPill.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    this.messagesPill.setInteractive({ useHandCursor: true });
    this.messagesPillLabel = this.add.text(pillLabelX, messagesPillY, 'Messages', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0, 0.5);
    this.messagesPill.on('pointerdown', () => this.setActiveView('messages'));

    this.rightHeaderTitle = this.add.text(rightX, headerTop, '', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.rightHeaderSubtitle = this.add.text(rightX, headerMessageY, '', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.heading,
      color: THEME.colors.text,
    }).setOrigin(0.5);

    this.messageSlots = getActiveMessageSlots();
    const maxLeftItems = 10;
    this.visibleMessageSlots = this.messageSlots.slice(0, maxLeftItems);

    for (let i = 0; i < this.visibleMessageSlots.length; i++) {
      const y = listTop + i * rowHeight;
      const entry = this.visibleMessageSlots[i];
      const bg = this.add.rectangle(leftX, y, messageRowWidth, 36, THEME.colors.backgroundHex, 0.9);
      bg.setStrokeStyle(2, THEME.colors.backgroundHex);
      bg.setInteractive({ useHandCursor: true });
      bg.setVisible(false);

      const label = this.add.text(messageLeft + 10, y, `${entry.slot + 1}. ${this.truncateMessage(entry.text)}`, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
      }).setOrigin(0, 0.5);
      label.setVisible(false);

      bg.on('pointerdown', () => {
        this.selectedMessageIndex = i;
        this.setActiveView('messages');
      });

      this.messageButtons.push({ bg, label });
    }

    this.dailyDates = buildRecentDailyDates(maxLeftItems);
    for (let i = 0; i < this.dailyDates.length; i++) {
      const y = listTop + i * rowHeight;
      const entry = this.dailyDates[i];
      const bg = this.add.rectangle(leftX, y, messageRowWidth, 36, THEME.colors.backgroundHex, 0.9);
      bg.setStrokeStyle(2, THEME.colors.backgroundHex);
      bg.setInteractive({ useHandCursor: true });

      const label = this.add.text(messageLeft + 10, y, entry.label, {
        fontFamily: THEME.fonts.body,
        fontSize: THEME.sizes.body,
        color: THEME.colors.textMuted,
      }).setOrigin(0, 0.5);

      bg.on('pointerdown', () => {
        this.selectedDateIndex = i;
        this.setActiveView('daily');
      });

      this.dateButtons.push({ bg, label });
    }

    this.createLeaderboardRows(rightX, listTop, headerRowY);

    this.statusText = this.add.text(rightX, listTop + rowHeight * 5, '', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.body,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    createBackButton(this, {
      x: 100,
      y: 60,
      onClick: () => this.scene.start('MenuScene'),
    });

    this.setActiveView('daily');
  }

  private createLeaderboardRows(rightX: number, listTop: number, headerRowY: number): void {
    const rowHeight = 44;
    const colRank = rightX - 220;
    const colName = rightX - 110;
    const colScore = rightX + 60;
    const colAcc = rightX + 170;
    const colGrade = rightX + 260;

    this.add.text(colRank, headerRowY, 'RANK', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.add.text(colName, headerRowY, 'PLAYER', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.add.text(colScore, headerRowY, 'SCORE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.add.text(colAcc, headerRowY, 'ACC', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);
    this.add.text(colGrade, headerRowY, 'GRADE', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.tiny,
      color: THEME.colors.textMuted,
    }).setOrigin(0.5);

    for (let i = 0; i < 10; i++) {
      const y = listTop + i * rowHeight;
      const bg = this.add.rectangle(rightX, y, 560, 36, THEME.colors.backgroundPanelHex, 0.75);
      bg.setStrokeStyle(2, THEME.colors.backgroundPanelHex);

      const row: LeaderboardRow = {
        bg,
        rank: this.add.text(colRank, y, '—', this.getRowTextStyle()).setOrigin(0.5).setVisible(false),
        name: this.add.text(colName, y, '—', this.getRowTextStyle()).setOrigin(0.5).setVisible(false),
        score: this.add.text(colScore, y, '—', this.getRowTextStyle()).setOrigin(0.5).setVisible(false),
        accuracy: this.add.text(colAcc, y, '—', this.getRowTextStyle()).setOrigin(0.5).setVisible(false),
        grade: this.add.text(colGrade, y, '—', this.getRowTextStyle()).setOrigin(0.5).setVisible(false),
      };
      bg.setVisible(false);

      this.rows.push(row);
    }
  }

  private getRowTextStyle(): Phaser.Types.GameObjects.Text.TextStyle {
    return {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    };
  }

  private setActiveView(view: LeaderboardView): void {
    this.activeView = view;
    this.updatePillStyles();
    this.updateLeftColumnVisibility();
    if (view === 'daily') {
      this.setSelectedDateIndex(this.selectedDateIndex);
    } else {
      this.setSelectedMessageIndex(this.selectedMessageIndex);
    }
  }

  private setSelectedMessageIndex(index: number, options?: { skipLoad?: boolean }): void {
    if (this.visibleMessageSlots.length === 0) {
      if (this.rightHeaderSubtitle) {
        this.rightHeaderSubtitle.setText('');
      }
      this.updateRightHeaderTitle();
      return;
    }
    if (index < 0 || index >= this.visibleMessageSlots.length) return;
    this.selectedMessageIndex = index;
    const entry = this.visibleMessageSlots[index];

    this.messageButtons.forEach((button, i) => {
      const isSelected = i === index;
      button.bg.setFillStyle(THEME.colors.backgroundHex, 0.9);
      button.bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.backgroundHex);
      button.label.setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
      button.bg.setVisible(this.activeView === 'messages');
      button.label.setVisible(this.activeView === 'messages');
    });

    if (this.rightHeaderSubtitle) {
      this.rightHeaderSubtitle.setText(this.truncateMessage(entry.text, 20));
    }

    this.updateRightHeaderTitle();

    if (options?.skipLoad) return;

    this.clearLeaderboard();
    this.loadLeaderboard(`single:${entry.slot}`);
  }

  private setSelectedDateIndex(index: number, options?: { skipLoad?: boolean }): void {
    if (this.dailyDates.length === 0) {
      if (this.rightHeaderSubtitle) {
        this.rightHeaderSubtitle.setText('');
      }
      this.updateRightHeaderTitle();
      return;
    }
    if (index < 0 || index >= this.dailyDates.length) return;
    this.selectedDateIndex = index;
    const entry = this.dailyDates[index];

    this.dateButtons.forEach((button, i) => {
      const isSelected = i === index;
      button.bg.setFillStyle(THEME.colors.backgroundHex, 0.9);
      button.bg.setStrokeStyle(2, isSelected ? THEME.colors.cyanHex : THEME.colors.backgroundHex);
      button.label.setColor(isSelected ? THEME.colors.text : THEME.colors.textMuted);
      button.bg.setVisible(this.activeView === 'daily');
      button.label.setVisible(this.activeView === 'daily');
    });

    if (this.rightHeaderSubtitle) {
      this.rightHeaderSubtitle.setText(formatDailyDisplayDate(entry.date));
    }

    this.updateRightHeaderTitle();

    if (options?.skipLoad) return;

    this.clearLeaderboard();
    this.loadLeaderboard(`daily:${entry.date}`);
  }

  private updatePillStyles(): void {
    if (!this.dailyPill || !this.dailyPillLabel || !this.messagesPill || !this.messagesPillLabel) return;
    const dailyActive = this.activeView === 'daily';
    const messagesActive = this.activeView === 'messages';
    // Always purple fill - stroke changes (cyan when active, border when inactive)
    // Like difficulty selector
    this.dailyPill.setFillStyle(THEME.colors.backgroundPanelHex, 1);
    this.dailyPill.setStrokeStyle(2, dailyActive ? THEME.colors.cyanHex : THEME.colors.borderHex);
    this.dailyPillLabel.setColor(dailyActive ? THEME.colors.text : THEME.colors.textMuted);
    this.messagesPill.setFillStyle(THEME.colors.backgroundPanelHex, 1);
    this.messagesPill.setStrokeStyle(2, messagesActive ? THEME.colors.cyanHex : THEME.colors.borderHex);
    this.messagesPillLabel.setColor(messagesActive ? THEME.colors.text : THEME.colors.textMuted);
  }

  private updateLeftColumnVisibility(): void {
    const showDaily = this.activeView === 'daily';
    this.dateButtons.forEach((button) => {
      button.bg.setVisible(showDaily);
      button.label.setVisible(showDaily);
    });
    this.messageButtons.forEach((button) => {
      button.bg.setVisible(!showDaily);
      button.label.setVisible(!showDaily);
    });
  }

  private updateRightHeaderTitle(): void {
    if (!this.rightHeaderTitle) return;
    this.rightHeaderTitle.setText('TOP RUNS');
  }

  private clearLeaderboard(): void {
    this.updateRows([]);
    if (this.statusText) {
      this.statusText.setText('Loading...');
      this.statusText.setVisible(true);
    }
  }

  private async loadLeaderboard(leaderboardKey: string): Promise<void> {
    if (!this.guildId) return;

    this.lastLoadedLeaderboardKey = leaderboardKey;

    const runs = await fetchLeaderboard({
      guildId: this.guildId,
      leaderboardType: leaderboardKey,
      limit: 10,
    });

    if (!runs) {
      if (this.statusText) {
        this.statusText.setText('Unable to load leaderboard.');
      }
      this.updateRows([]);
      return;
    }

    this.updateRows(runs);
    this.updateStatus(runs.length, null);

    this.ensureAutoRefresh();
  }

  private updateRows(runs: LeaderboardRun[]): void {
    for (let i = 0; i < this.rows.length; i++) {
      const row = this.rows[i];
      const run = runs[i];
      const rank = i + 1;

      if (!run) {
        row.bg.setVisible(false);
        row.rank.setVisible(false);
        row.name.setVisible(false);
        row.score.setVisible(false);
        row.accuracy.setVisible(false);
        row.grade.setVisible(false);
        continue;
      }

      row.bg.setVisible(true);
      row.rank.setVisible(true);
      row.name.setVisible(true);
      row.score.setVisible(true);
      row.accuracy.setVisible(true);
      row.grade.setVisible(true);

      row.rank.setText(String(rank));
      row.name.setText(run.discord_name);
      row.score.setText(this.formatScore(run.score));
      row.accuracy.setText(this.formatAccuracy(run.metadata));
      row.grade.setText(this.formatGrade(run.metadata));
    }
  }

  private updateStatus(count: number, errorMessage: string | null): void {
    if (!this.statusText) return;
    if (errorMessage) {
      this.statusText.setText(errorMessage);
      this.statusText.setColor(THEME.colors.textMuted);
      this.statusText.setVisible(true);
      return;
    }
    if (count === 0) {
      this.statusText.setText('No runs yet.');
      this.statusText.setColor(THEME.colors.textMuted);
      this.statusText.setVisible(true);
      return;
    }
    this.statusText.setText('');
    this.statusText.setVisible(false);
  }

  private ensureAutoRefresh(): void {
    if (this.refreshTimer) return;
    this.refreshTimer = this.time.addEvent({
      delay: 10000,
      loop: true,
      callback: () => {
        if (!this.lastLoadedLeaderboardKey) return;
        void this.loadLeaderboard(this.lastLoadedLeaderboardKey);
      },
    });
  }

  private formatScore(score: number | string): string {
    const value = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(value)) return '—';
    return Math.round(value).toLocaleString();
  }

  private formatAccuracy(metadata?: Record<string, unknown> | null): string {
    const raw = metadata?.accuracy;
    const value = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(value)) return '—';
    return `${value.toFixed(2)}%`;
  }

  private formatGrade(metadata?: Record<string, unknown> | null): string {
    const raw = metadata?.grade;
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    return '—';
  }

  private truncateMessage(message: string, maxLength: number = 10): string {
    const trimmed = message.trim();
    if (trimmed.length <= maxLength) return trimmed;
    return `${trimmed.slice(0, maxLength)}...`;
  }
}

function buildRecentDailyDates(count: number): DailyDateEntry[] {
  const entries: DailyDateEntry[] = [];
  let currentDate = getDailyLosAngelesDate();
  for (let i = 0; i < count; i++) {
    entries.push({ date: currentDate, label: formatDailyLeftLabel(currentDate) });
    currentDate = subtractDailyDate(currentDate, 1);
  }
  return entries;
}

function subtractDailyDate(dateString: string, days: number): string {
  const date = parseDailyDate(dateString);
  if (!date) return dateString;
  date.setUTCDate(date.getUTCDate() - days);
  return formatDateUTC(date);
}

function parseDailyDate(dateString: string): Date | null {
  const [yearText, monthText, dayText] = dateString.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (!year || !month || !day) return null;
  return new Date(Date.UTC(year, month - 1, day));
}

function formatDateUTC(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDailyLeftLabel(dateString: string): string {
  const date = parseDailyDate(dateString);
  if (!date) return dateString;
  const month = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' }).format(date);
  const weekday = new Intl.DateTimeFormat('en-US', { weekday: 'short', timeZone: 'UTC' }).format(date);
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${month} ${day} • ${weekday}`;
}
